import { memo, useContext, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@zg-rentals/react-query5';

import { Box, Checkbox, FieldSet, FormHelp, Label, LabeledControl, Legend, Radio } from '@zillow/constellation-10';
import { debounce } from '@zg-rentals/zdash';

import { useSelector } from '../../../../selectors/useSelector';

import TimeConstants from '../../../../constants/TimeConstants';
import WizardServerError from '../../../../model/WizardServerError';
import { ModularListingContext } from '../../../../containers/CreateListingPage/context';
import { WizardContext } from '../../../../components/Wizard/context';
import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';

import { useForm } from '../../../../hooks';

import { postingConstraints } from '../../../../utils/formValidation';
import { getDataToSave } from '../../../../utils/amenitiesUtils';

import PropertiesActions from '../../../../actions/PropertiesActions';

import Amenities from '../../../../model/Amenities';

import actions from '../../../../containers/CreateListingPage/actions';

import wizardActions from '../../../../components/Wizard/actions';
import WizardAlert from '../../../../components/WizardAlert';

import type { ChangeEvent } from 'react';
import type { AmenitiesProps } from '../../../../model/Amenities';

const saveDebouncedViaMultiEndpoint = debounce(
  (saveViaMultiEndpoint: () => void) => saveViaMultiEndpoint(),
  TimeConstants.SELECTION_DEBOUNCE_TIME_MS,
);

const AmenitiesInteriorFormField = () => {
  const dispatch = useDispatch();

  const queryClient = useQueryClient();

  const { reducerWizardDispatch } = useContext(WizardContext);
  const {
    modularListingState: { isInModalState, isSaveComplete, serverErrorObj, shouldSaveOnBlur },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const {
    properties: {
      currentProperty: {
        amenities: { interior },
        listingAlias,
      },
    },
  } = useSelector((state) => state);

  const { initialInputs, categorizedAmenitiesKeys } = useMemo(() => {
    const categorizedAmenitiesKeys: Record<string, Array<string>> = { checkboxes: [] as Array<string> };
    const initialInputs: Record<string, boolean | string> = interior.reduce(
      (a: Record<string, boolean | string>, { category, isUniqueSelection, values }: AmenitiesProps['interior'][0]) => {
        if (isUniqueSelection) {
          categorizedAmenitiesKeys[category] = [];
        }

        values.forEach(({ fieldName, isSelected }) => {
          if (!isUniqueSelection) {
            a[fieldName] = isSelected;
          } else if (isSelected) {
            a[category] = fieldName;
          }

          if (isUniqueSelection) {
            categorizedAmenitiesKeys[category].push(fieldName);
          } else {
            categorizedAmenitiesKeys.checkboxes.push(fieldName);
          }
        });

        return a;
      },
      {},
    );
    return { initialInputs, categorizedAmenitiesKeys };
  }, [listingAlias]);

  const {
    inputs,
    hasFieldError,
    handleChange: handleFormChange,
    addErrorKey,
    errorObj,
  } = useForm(initialInputs, postingConstraints);

  useEffect(() => {
    if (isSaveComplete) {
      if (!inputs.Laundry) {
        actions.updateHasFieldError({ reducerDispatch, hasFieldError: true });
        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });

        addErrorKey('Laundry', 'Please enter laundry facility information.');

        return;
      }

      // eslint-disable-next-line no-use-before-define
      saveViaMultiEndpoint({
        // @ts-expect-error
        dataToSave: getDataToSave({ inputs, categorizedAmenitiesKeys, listingAlias }),
      });
    }
  }, [isSaveComplete]);

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  // @ts-expect-error
  const saveViaMultiEndpoint = ({ dataToSave }) => {
    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    queryClient.invalidateQueries({ queryKey: ['getListingRecommendations', listingAlias] });

    return (
      dispatch(PropertiesActions.saveMultipleFieldsPropertyInfo(dataToSave))
        // @ts-expect-error
        .then((response) => {
          const {
            listingOutputFieldData: { amenities, laundry },
          } = response;

          dispatch(PropertiesActions.updateCurrentProperty({ amenities: Amenities(amenities), laundry }));

          actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
        })
        // @ts-expect-error
        .catch((message) => {
          updateServerError(message, 'multi');
        })
        .finally(() => {
          wizardActions.updateIsFetchingData({
            reducerWizardDispatch,
            isFetchingData: false,
          });
        })
    );
  };

  const save = (event: ChangeEvent<HTMLInputElement>) => {
    const { name: fieldName, value } = event.target;

    const valueToSave = categorizedAmenitiesKeys[fieldName] ? value : value === 'true';

    actions.updateHasFieldError({ reducerDispatch, hasFieldError: false });

    clearServerError();
    handleFormChange({ target: { name: fieldName, value: valueToSave } });

    // return if user must click on cta
    if (!shouldSaveOnBlur && !isSaveComplete) {
      return;
    }

    // @ts-expect-error
    if (hasFieldError('multi')) {
      return;
    }

    saveDebouncedViaMultiEndpoint(() => {
      // @ts-expect-error
      const dataToSave = getDataToSave({ inputs, categorizedAmenitiesKeys, listingAlias, fieldName, valueToSave });

      saveViaMultiEndpoint({ dataToSave });
    });
  };

  const updateServerError = (message = '', fieldName = '') => {
    const displayAt = isInModalState ? DISPLAY_SERVER_ERROR_AT.FIELD : DISPLAY_SERVER_ERROR_AT.PAGE;

    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  return (
    <div data-test="AmenitiesInteriorFormField">
      <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.FIELD} />

      <Box
        data-testid="AmenitiesInteriorFormFieldContent"
        css={{ display: 'flex', flexDirection: 'row', gap: '400', flexWrap: 'wrap' }}
      >
        {interior.map(
          ({ category, isEditable, isRequired, isUniqueSelection, values }: AmenitiesProps['interior'][0]) => (
            <Box
              key={`${category}FieldSetContainer`}
              css={{ display: 'flex', flexDirection: 'column', gap: '400', flex: '1 0 280px' }}
            >
              <FieldSet
                disabled={!isEditable}
                required={isRequired && isEditable}
                legend={<Legend>{category}</Legend>}
                // @ts-expect-error
                error={!!errorObj[category]}
                // @ts-expect-error
                description={<FormHelp>{errorObj[category]}</FormHelp>}
              >
                {values.map(({ displayName, fieldName }) => {
                  const ControlInput = isUniqueSelection ? Radio : Checkbox;

                  return (
                    <LabeledControl
                      key={displayName}
                      label={<Label>{displayName}</Label>}
                      control={
                        <ControlInput
                          name={isUniqueSelection ? category : fieldName}
                          value={isUniqueSelection ? fieldName : String(!inputs[fieldName])}
                          checked={isUniqueSelection ? inputs[category] === fieldName : Boolean(inputs[fieldName])}
                          onChange={save}
                        />
                      }
                    />
                  );
                })}
              </FieldSet>
            </Box>
          ),
        )}
      </Box>
    </div>
  );
};

export default memo(AmenitiesInteriorFormField);
